import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, X } from 'lucide-react';

const DateRangePicker = ({ startDate, endDate, onChange, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(startDate || '');
  const [tempEndDate, setTempEndDate] = useState(endDate || '');
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    setTempStartDate(startDate || '');
    setTempEndDate(endDate || '');
  }, [startDate, endDate]);

  // Calculate dropdown position - fixed to viewport, doesn't scroll
  useEffect(() => {
    const updatePosition = () => {
      if (isOpen && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const dropdownWidth = 320; // min-w-[320px]
        const dropdownHeight = 300; // approximate height
        
        let top = rect.bottom + 8;
        let left = rect.left;
        
        // Adjust if dropdown would go off screen
        if (left + dropdownWidth > viewportWidth) {
          left = viewportWidth - dropdownWidth - 16; // 16px padding from edge
        }
        if (left < 16) {
          left = 16;
        }
        
        // Adjust if dropdown would go below viewport
        if (top + dropdownHeight > viewportHeight) {
          top = rect.top - dropdownHeight - 8; // Show above button instead
        }
        if (top < 16) {
          top = 16;
        }
        
        setPosition({
          top: top,
          left: left
        });
      }
    };

    updatePosition();

    // Update position on scroll/resize to keep it aligned with button
    if (isOpen) {
      const handleScroll = () => updatePosition();
      const handleResize = () => updatePosition();
      
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current && 
        !containerRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleApply = () => {
    if (tempStartDate && tempEndDate) {
      if (new Date(tempStartDate) > new Date(tempEndDate)) {
        alert('Start date must be before end date');
        return;
      }
      onChange(tempStartDate, tempEndDate);
      setIsOpen(false);
    } else if (tempStartDate || tempEndDate) {
      alert('Please select both start and end dates');
    }
  };

  const handleClear = () => {
    setTempStartDate('');
    setTempEndDate('');
    onChange('', '');
    setIsOpen(false);
  };

  const getDisplayText = () => {
    if (startDate && endDate) {
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    }
    return 'Select date range';
  };

  // Get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split('T')[0];

  const dropdownContent = isOpen && (
    <div 
      ref={dropdownRef}
      className="fixed z-[99999] bg-card/95 backdrop-blur-md border border-border rounded-xl shadow-lg p-4 min-w-[320px]"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`
      }}
    >
      <style>{`
        /* Style the native date picker calendar popup */
        input[type="date"]::-webkit-calendar-picker-indicator {
          cursor: pointer;
          filter: ${document.documentElement.classList.contains('dark') ? 'invert(1)' : 'invert(0)'};
          opacity: 0.8;
        }
        input[type="date"]::-webkit-calendar-picker-indicator:hover {
          opacity: 1;
        }
        input[type="date"]::-webkit-datetime-edit-text {
          color: hsl(var(--foreground));
        }
        input[type="date"]::-webkit-datetime-edit-month-field,
        input[type="date"]::-webkit-datetime-edit-day-field,
        input[type="date"]::-webkit-datetime-edit-year-field {
          color: hsl(var(--foreground));
        }
        input[type="date"]::-webkit-datetime-edit-month-field:focus,
        input[type="date"]::-webkit-datetime-edit-day-field:focus,
        input[type="date"]::-webkit-datetime-edit-year-field:focus {
          background-color: hsl(var(--primary) / 0.2);
          color: hsl(var(--primary));
        }
      `}</style>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Start Date
          </label>
          <input
            type="date"
            value={tempStartDate}
            onChange={(e) => setTempStartDate(e.target.value)}
            max={today}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary dark:bg-card dark:border-border dark:text-foreground"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            End Date
          </label>
          <input
            type="date"
            value={tempEndDate}
            onChange={(e) => setTempEndDate(e.target.value)}
            min={tempStartDate || undefined}
            max={today}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary dark:bg-card dark:border-border dark:text-foreground"
          />
        </div>
        <div className="flex gap-2 pt-2">
          <button
            onClick={handleApply}
            className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            Apply
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="px-4 py-2 bg-muted/50 text-foreground rounded-lg hover:bg-muted transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2 bg-card border border-border rounded-lg hover:bg-muted/50 transition-colors flex items-center justify-between gap-2 text-foreground dark:bg-card dark:border-border dark:hover:bg-muted/50"
      >
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-white dark:text-white" />
          <span className={startDate && endDate ? 'text-foreground' : 'text-muted-foreground'}>
            {getDisplayText()}
          </span>
        </div>
        {startDate && endDate && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            className="hover:bg-muted rounded p-1"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </button>

      {typeof window !== 'undefined' && createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default DateRangePicker;

